import { useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Loader2, MessageSquareText, Search, SlidersHorizontal } from 'lucide-react'

import { useDebounce } from '@/components/hooks/use-debounce'
import { useDirtyClose } from '@/hooks/use-dirty-close'
import {
  useCreateBoatWant,
  usePreviewBoatWant,
  useUpdateBoatWant,
} from '@/features/customers/boat-want-hooks'
import { boatMatchName } from '@/features/customers/boat-want-utils'
import { BOAT_TYPES, BOAT_TYPE_LABELS, type BoatType } from '@/types/inventory'
import type { BoatMatch, BoatWant, WantCriteria } from '@/types/boat-want'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

interface BoatWantDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  customerId: string
  customerFirstName?: string
  customerPhone?: string | null
  // Set to edit an existing want; null/undefined creates a new one.
  want?: BoatWant | null
}

interface FormState {
  raw_text: string
  notify_enabled: boolean
  criteria: WantCriteria
}

// numOrNull: a criteria numeric field from an input; empty string clears it.
function numOrNull(v: string): number | null {
  if (v.trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

// normalizeCriteria mirrors the server: an inverted min/max pair is swapped
// so a hand-entered window never becomes empty.
function normalizeCriteria(c: WantCriteria): WantCriteria {
  const out = { ...c }
  if (
    out.min_length_ft != null &&
    out.max_length_ft != null &&
    out.min_length_ft > out.max_length_ft
  ) {
    ;[out.min_length_ft, out.max_length_ft] = [out.max_length_ft, out.min_length_ft]
  }
  if (out.min_year != null && out.max_year != null && out.min_year > out.max_year) {
    ;[out.min_year, out.max_year] = [out.max_year, out.min_year]
  }
  if (out.min_price != null && out.max_price != null && out.min_price > out.max_price) {
    ;[out.min_price, out.max_price] = [out.max_price, out.min_price]
  }
  return out
}

export function BoatWantDialog({
  open,
  onOpenChange,
  customerId,
  customerFirstName,
  customerPhone,
  want,
}: BoatWantDialogProps) {
  const createMutation = useCreateBoatWant()
  const updateMutation = useUpdateBoatWant()
  const previewMutation = usePreviewBoatWant()

  const [form, setForm] = useState<FormState>({ raw_text: '', notify_enabled: false, criteria: {} })
  const [showRefine, setShowRefine] = useState(false)
  // Snapshot of the seeded values — anything else means unsaved edits.
  const [seed, setSeed] = useState<FormState | null>(null)

  // Reset whenever the dialog opens or the target want changes — implemented
  // as "adjust state during render" (see React docs) like DealEditDialog.
  const formKey = `${open}:${want?.id ?? ''}`
  const [prevFormKey, setPrevFormKey] = useState(formKey)
  if (prevFormKey !== formKey) {
    setPrevFormKey(formKey)
    if (open && want) {
      const seeded: FormState = {
        raw_text: want.raw_text,
        notify_enabled: want.notify_enabled,
        criteria: want.criteria,
      }
      setForm(seeded)
      setSeed(seeded)
      setShowRefine(true)
    } else if (open) {
      const seeded: FormState = { raw_text: '', notify_enabled: false, criteria: {} }
      setForm(seeded)
      setSeed(seeded)
      setShowRefine(false)
    }
  }

  const isDirty = seed !== null && JSON.stringify(form) !== JSON.stringify(seed)
  const { guardOpenChange, discardGuard } = useDirtyClose(open, onOpenChange, isDirty)

  // Live preview as the salesperson types: the text is re-parsed server-side
  // and current for-sale inventory is scored against it. Criteria edits
  // re-score without re-parsing (the sent criteria overrides the parse,
  // matching the backend's merge) — and an edit-mode open must NOT re-parse,
  // or it would clobber hand-adjusted criteria. Matches come straight from
  // the mutation's result — no mirrored state.
  const debouncedRaw = useDebounce(form.raw_text.trim(), 400)
  const lastParsedRaw = useRef<string | null>(null)
  const previewCriteriaJson = useRef<string | null>(null)
  const debouncedCriteriaJson = useDebounce(JSON.stringify(form.criteria), 400)

  const runPreview = (
    body: { raw_text: string; criteria?: WantCriteria },
    applyCriteria: boolean,
  ) => {
    previewMutation.mutate(body, {
      onSuccess: (data) => {
        if (applyCriteria) {
          previewCriteriaJson.current = JSON.stringify(data.criteria)
          setForm((f) => ({ ...f, criteria: data.criteria }))
        }
      },
    })
  }

  useEffect(() => {
    if (!open) return
    if (lastParsedRaw.current === null && want) {
      // Edit mode: criteria were seeded from the stored want; the seeded text
      // counts as already parsed (the matches preview runs below instead).
      lastParsedRaw.current = debouncedRaw
      return
    }
    if (debouncedRaw === lastParsedRaw.current) return
    lastParsedRaw.current = debouncedRaw
    if (debouncedRaw === '') return
    // Fresh text: re-parse (criteria comes back from the response).
    runPreview({ raw_text: debouncedRaw }, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRaw, open])

  useEffect(() => {
    if (!open) return
    // Skip the echo of a criteria set we just received from a parse, and
    // don't score before there is anything to score.
    if (previewCriteriaJson.current === debouncedCriteriaJson) return
    if (debouncedRaw === '') return
    runPreview(
      { raw_text: debouncedRaw, criteria: JSON.parse(debouncedCriteriaJson) as WantCriteria },
      false,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCriteriaJson, open])

  // Scored inventory for the current query — empty until a preview exists.
  // The trailing equality check hides the stale preview from the previous
  // dialog session during the ~400ms the debounce lags behind a reopen.
  const hasQuery = open && form.raw_text.trim() !== '' && debouncedRaw === form.raw_text.trim()
  const matches = hasQuery ? (previewMutation.data?.matches ?? []) : []

  const pending = previewMutation.isPending
  const saving = createMutation.isPending || updateMutation.isPending

  const setCriteriaField = (patch: Partial<WantCriteria>) => {
    setForm((f) => ({ ...f, criteria: normalizeCriteria({ ...f.criteria, ...patch }) }))
  }

  const handleSave = () => {
    const body = {
      raw_text: form.raw_text.trim(),
      notify_enabled: form.notify_enabled,
      criteria: normalizeCriteria(form.criteria),
    }
    if (want) {
      updateMutation.mutate({ id: want.id, body }, { onSuccess: () => onOpenChange(false) })
    } else {
      createMutation.mutate(
        { customer_id: customerId, ...body },
        { onSuccess: () => onOpenChange(false) },
      )
    }
  }

  const error = createMutation.error?.message ?? updateMutation.error?.message

  return (
    <Dialog open={open} onOpenChange={guardOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{want ? 'Edit Boat Want' : 'What Are They Looking For?'}</DialogTitle>
          <DialogDescription>
            Describe the boat in the customer's own words — "24' wake boat with low hours" works as
            well as an exact model. You'll see the closest matches in current inventory.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="want-raw-text">What the customer is looking for</Label>
            <Textarea
              id="want-raw-text"
              placeholder="e.g. 24' wake boat with low hours  ·  2020-2022 Malibu Wakesetter 23 LSV under 300 hours  ·  used pontoon under $30k"
              value={form.raw_text}
              onChange={(e) => setForm((f) => ({ ...f, raw_text: e.target.value }))}
              rows={3}
            />
            {pending && (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Reading what they're looking for…
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Details</Label>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => setShowRefine((v) => !v)}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                {showRefine ? 'Hide' : 'Refine'}
              </Button>
            </div>
            {!showRefine && (
              <p className="text-xs text-muted-foreground">
                Parsed automatically from the text above. Refine anything the parser got wrong.
              </p>
            )}
            {showRefine && (
              <div className="grid gap-3 rounded-lg border p-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Type</Label>
                  <Select
                    value={form.criteria.boat_type ?? 'none'}
                    onValueChange={(v) => setCriteriaField({ boat_type: v === 'none' ? null : v })}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Any type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Any type</SelectItem>
                      {BOAT_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {BOAT_TYPE_LABELS[t as BoatType] ?? t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Condition</Label>
                  <Select
                    value={form.criteria.condition ?? 'none'}
                    onValueChange={(v) => setCriteriaField({ condition: v === 'none' ? null : v })}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Either" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Either</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="used">Used</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Make</Label>
                  <Input
                    className="h-8"
                    value={form.criteria.make ?? ''}
                    onChange={(e) => setCriteriaField({ make: e.target.value })}
                    placeholder="e.g. Malibu"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Model contains</Label>
                  <Input
                    className="h-8"
                    value={form.criteria.model ?? ''}
                    onChange={(e) => setCriteriaField({ model: e.target.value })}
                    placeholder="e.g. Wakesetter"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Length (ft)</Label>
                  <div className="flex gap-2">
                    <Input
                      className="h-8"
                      type="number"
                      value={form.criteria.min_length_ft ?? ''}
                      onChange={(e) =>
                        setCriteriaField({ min_length_ft: numOrNull(e.target.value) })
                      }
                      placeholder="min"
                    />
                    <Input
                      className="h-8"
                      type="number"
                      value={form.criteria.max_length_ft ?? ''}
                      onChange={(e) =>
                        setCriteriaField({ max_length_ft: numOrNull(e.target.value) })
                      }
                      placeholder="max"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Year</Label>
                  <div className="flex gap-2">
                    <Input
                      className="h-8"
                      type="number"
                      value={form.criteria.min_year ?? ''}
                      onChange={(e) => setCriteriaField({ min_year: numOrNull(e.target.value) })}
                      placeholder="from"
                    />
                    <Input
                      className="h-8"
                      type="number"
                      value={form.criteria.max_year ?? ''}
                      onChange={(e) => setCriteriaField({ max_year: numOrNull(e.target.value) })}
                      placeholder="to"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Min price ($)</Label>
                  <Input
                    className="h-8"
                    type="number"
                    value={form.criteria.min_price ?? ''}
                    onChange={(e) => setCriteriaField({ min_price: numOrNull(e.target.value) })}
                    placeholder="e.g. 60000"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Max price ($)</Label>
                  <Input
                    className="h-8"
                    type="number"
                    value={form.criteria.max_price ?? ''}
                    onChange={(e) => setCriteriaField({ max_price: numOrNull(e.target.value) })}
                    placeholder="e.g. 80000"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Max engine hours</Label>
                  <Input
                    className="h-8"
                    type="number"
                    value={form.criteria.max_engine_hours ?? ''}
                    onChange={(e) =>
                      setCriteriaField({ max_engine_hours: numOrNull(e.target.value) })
                    }
                    placeholder="e.g. 250"
                  />
                </div>
              </div>
            )}
          </div>

          <MatchResults matches={matches} pending={pending} hasText={form.raw_text.trim() !== ''} />

          <div className="flex items-start gap-2 rounded-lg border p-3">
            <Checkbox
              id="want-notify"
              checked={form.notify_enabled}
              onCheckedChange={(v) => setForm((f) => ({ ...f, notify_enabled: v === true }))}
              disabled={!customerPhone}
            />
            <div className="space-y-0.5">
              <Label
                htmlFor="want-notify"
                className="flex items-center gap-1.5 text-sm font-normal"
              >
                <MessageSquareText className="h-3.5 w-3.5" />
                Text {customerFirstName || 'the customer'} when a matching boat arrives
              </Label>
              {customerPhone ? (
                <p className="text-xs text-muted-foreground">
                  To {customerPhone} — confirm the customer agreed to receive texts.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Add a phone number to the customer record to enable text alerts.
                </p>
              )}
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={form.raw_text.trim() === '' || saving}>
            {saving ? 'Saving…' : want ? 'Save Changes' : 'Save'}
          </Button>
        </DialogFooter>
        {discardGuard}
      </DialogContent>
    </Dialog>
  )
}

function MatchResults({
  matches,
  pending,
  hasText,
}: {
  matches: BoatMatch[]
  pending: boolean
  hasText: boolean
}) {
  if (pending && matches.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border p-4 text-sm text-muted-foreground">
        <Search className="h-4 w-4" /> Searching current inventory…
      </div>
    )
  }
  if (!hasText) {
    return (
      <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        Matches in current inventory will appear here as you type.
      </p>
    )
  }
  if (matches.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        Nothing in current inventory fits. Save the want with text alerts on and the customer will
        be the first to know when one arrives.
      </div>
    )
  }
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        Closest matches in current inventory ({matches.length})
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {matches.map((m) => (
          <Link
            key={m.boat_id}
            to="/inventory/$boatId"
            params={{ boatId: m.boat_id }}
            className="group"
          >
            <div className="flex gap-3 rounded-lg border p-2 transition-colors group-hover:bg-muted/50">
              {m.cover_thumb_url ? (
                <img
                  src={m.cover_thumb_url}
                  alt=""
                  className="h-14 w-20 shrink-0 rounded-md object-cover"
                />
              ) : (
                <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <Search className="h-4 w-4" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{boatMatchName(m)}</div>
                <div className="text-xs text-muted-foreground">
                  {m.length_ft > 0 ? `${m.length_ft} ft · ` : ''}
                  {m.engine_hours != null ? `${m.engine_hours} hrs · ` : ''}
                  {m.condition}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  <Badge variant="secondary" className="text-[10px]">
                    {m.score >= 60
                      ? 'Strong match'
                      : m.score >= 40
                        ? 'Good match'
                        : 'Possible match'}
                  </Badge>
                  {m.reasons.slice(0, 2).map((r) => (
                    <span key={r} className="truncate text-[11px] text-muted-foreground">
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
